import { EnvironmentOutlined } from "@ant-design/icons";
import { Progress, Tag } from "antd";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { stayData } from "../Data/data";
import useNetworkStatus from "../hooks/useNetworkStatus";
import useUTCTime from "../hooks/useUTCTime";

function VoyageStatus({ voyageDetails }) {
  const utcTime = useUTCTime();
  const isOnline = useNetworkStatus();
  const [percent, setPercent] = useState(0);
  const trackingStages = ["Departure", "In Transit", "Arrival"];
  // console.log("voyageDetails *********", voyageDetails);

  useEffect(() => {
    const interval = setInterval(() => {
      setPercent((prevPercent) => {
        if (prevPercent >= 100) {
          return 0;
        }
        return prevPercent + 0.1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
         <div>
            <h1 style={styles.header}>Voyage Status</h1>
            <h2 style={styles.subHeader}>
              VOYAGE NO. {voyageDetails?.voyage_no || "N/A"}
            </h2>
         </div>
         <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
             <Tag color={isOnline ? "success" : "error"} style={{ padding: '6px 16px', borderRadius: '20px', fontSize: '14px', fontWeight: 600, border: 'none' }}>
                 {isOnline ? "🟢 Online Sync Active" : "🔴 Offline"}
             </Tag>
             {!isOnline && (
                <Tag color="warning" style={{ borderRadius: '20px', border: 'none' }}>📦 Serving Database Backup</Tag>
             )}
         </div>
      </div>

      <div
        style={{ display: "flex", alignItems: "center", position: "relative", marginBottom: "20px" }}
      >
        <EnvironmentOutlined
          style={{ fontSize: "24px", marginRight: "16px", color: "#001529" }}
        />
        <div style={{ position: "relative", width: "100%" }}>
          <Progress percent={percent} status="active" showInfo={false} strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }} strokeWidth={8} />
          <motion.div
            style={{
              position: "absolute",
              left: `${percent}%`,
              top: "-24px",
              transform: "translateX(-50%)",
            }}
            animate={{ left: `${percent}%` }}
            transition={{ type: "keyframes", stiffness: 100, damping: 10 }}
          >
            <div className="pulse-icon" style={{ padding: '2px' }}>
              <EnvironmentOutlined style={{ fontSize: "28px", color: "#E11236" }} />
            </div>
          </motion.div>
        </div>
        <EnvironmentOutlined
          style={{ fontSize: "24px", marginLeft: "16px", color: "#10b981" }}
        />
      </div>

      <div style={styles.trackingLine}>
        {trackingStages.map((stage, index) => (
          <div key={index} style={styles.trackingItem}>
            <div
              style={{
                ...styles.trackingDot,
                ...(percent >= (index / (trackingStages.length - 1)) * 100
                  ? styles.trackingDotActive
                  : {}),
              }}
            />
            <span style={{ fontSize: "12px", fontWeight: 600, color: "#6b7280", marginTop: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{stage}</span>
          </div>
        ))}
      </div>

      <div style={{ marginTop: "16px", display: "grid", gridTemplateColumns: "1fr", gap: "8px", padding: "4px", flex: 1 }}>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-box glass-box-red" style={{ padding: "12px 16px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={styles.section}>
            <p style={styles.paragraph}><span style={styles.boldText}>🚢 Voyage No</span> <span style={styles.valueText}>{voyageDetails?.voyage_no || "N/A"}</span></p>
            <p style={styles.paragraph}><span style={styles.boldText}>Route</span> <span style={{ ...styles.valueText, textAlign: "right", marginLeft: "10px" }}>{voyageDetails?.load_ports?.port_name || "N/A"} → {voyageDetails?.discharge_ports?.port_name || "N/A"}</span></p>
            <p style={styles.paragraph}><span style={styles.boldText}>Status / Stay</span> <span style={styles.valueText}>{stayData.find((opt) => opt.value === voyageDetails?.stay)?.label || "N/A"}</span></p>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="glass-box glass-box-blue" style={{ padding: "12px 16px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={styles.section}>
            <p style={styles.paragraph}><span style={styles.boldText}>📍 Position</span> <span style={styles.valueText}>{voyageDetails?.latitude || "N/A"}, {voyageDetails?.longitude || "N/A"}</span></p>
            <p style={styles.paragraph}><span style={styles.boldText}>Course (COG)</span> <span style={styles.valueText}>{voyageDetails?.course || "N/A"}</span></p>
            <p style={styles.paragraph}><span style={styles.boldText}>Speed (SOG)</span> <span style={styles.valueText}>{voyageDetails?.speed_gps || "N/A"} kts</span></p>
          </div>
        </motion.div>
        
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="glass-box glass-box-amber" style={{ padding: "12px 16px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={styles.section}>
            <p style={{ ...styles.paragraph, flexDirection: "column", alignItems: "center", gap: "8px" }}>
              <span style={styles.boldText}>🕐 Target UTC Time</span> 
              <span style={{ ...styles.valueText, fontSize: "20px", color: "#E11236" }}>{utcTime}</span>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

const styles = {
  header: { textAlign: "left", fontSize: "28px", fontWeight: "800", color: "#0f172a", margin: 0, letterSpacing: "-0.5px" },
  subHeader: { textAlign: "left", fontSize: "15px", fontWeight: "500", color: "#64748b", margin: "4px 0 0 0", textTransform: "uppercase", letterSpacing: "1px" },
  section: { display: "flex", flexDirection: "column", gap: "8px" },
  paragraph: { margin: 0, display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  boldText: { fontWeight: "600", color: "#64748b", fontSize: "14px" },
  valueText: { fontWeight: "700", color: "#1e293b", fontSize: "14px", textAlign: "right", paddingLeft: "12px", wordBreak: "break-word" },
  trackingLine: { display: "flex", justifyContent: "space-between", margin: "24px 0", padding: "0 10px" },
  trackingItem: { textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" },
  trackingDot: { width: "16px", height: "16px", borderRadius: "50%", backgroundColor: "#e2e8f0", margin: "5px", transition: "all 0.3s ease", border: "2px solid #fff", boxShadow: "0 0 0 1px #cbd5e1" },
  trackingDotActive: { backgroundColor: "#E11236", transform: "scale(1.2)", boxShadow: "0 0 0 4px rgba(225, 18, 54, 0.2)", border: "2px solid #fff" },
};

export default VoyageStatus;
